import { useCallback, useEffect, useState } from "react";
import { isAuthenticated } from "../apicalls/auth";
import { getCoachMatchActivity, getCoachMatchRequests, updateCoachMatchRequest } from "../apicalls/coachMatchRequests";
import CoachFunnel from "./CoachFunnel";

const formatDate = (value) => value ? new Date(value).toLocaleString() : "-";
const activityLabel = ({ type, kind }) => {
  if (type !== "coach_connection_attempt") return "Completed quiz";
  return kind === "roster_request" ? "Asked to join roster" : "Tried to connect";
};

function CoachMatchRequests() {
  const token = isAuthenticated()?.access_token;
  const [activeTab, setActiveTab] = useState("queue");
  const [filters, setFilters] = useState({
    page: "1",
    perPage: "50",
    status: "new",
    search: "",
    hasConfirmedLesson: "false",
    submittedSince: "",
    activityType: "all",
  });
  const [data, setData] = useState({ requests: [], total: 0 });
  const [activityData, setActivityData] = useState({ activities: [], total: 0 });
  const [notes, setNotes] = useState({});
  const [error, setError] = useState("");
  const total = activeTab === "activity" ? activityData.total : data.total;
  const page = Math.max(1, Number(filters.page) || 1);
  const perPage = Math.max(1, Number(filters.perPage) || 50);
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const canPageBack = page > 1;
  const canPageForward = page < totalPages;
  const load = useCallback(async () => {
    if (!token || activeTab === "funnel") return;
    try {
      setError("");
      if (activeTab === "activity") {
        const { page, perPage, search, submittedSince, activityType } = filters;
        setActivityData(await getCoachMatchActivity({ page, perPage, search, submittedSince, type: activityType }, token));
        return;
      }
      setData(await getCoachMatchRequests(filters, token));
    }
    catch (err) { setError(err.message || "Could not load coach requests"); }
  }, [activeTab, filters, token]);
  useEffect(() => { load(); }, [load]);
  const save = async (request, status) => {
    try {
      await updateCoachMatchRequest(request.id, { status, outreachNotes: notes[request.id] ?? request.outreachNotes ?? "" }, token);
      load();
    } catch (err) { setError(err.message || "Could not save outreach update"); }
  };
  const showTab = (tab) => {
    setActiveTab(tab);
    setFilters((current) => ({ ...current, page: "1" }));
  };
  const updateFilters = (changes) => setFilters((current) => ({ ...current, page: "1", ...changes }));
  const goToPage = (nextPage) => setFilters((current) => ({ ...current, page: String(nextPage) }));
  const pagination = <div className="d-flex flex-wrap align-items-center mb-3">
    <button className="btn btn-outline-secondary btn-sm mr-2 mb-2" disabled={!canPageBack} onClick={() => goToPage(page - 1)}>Previous</button>
    <span className="mr-2 mb-2">Page {page} of {totalPages}</span>
    <button className="btn btn-outline-secondary btn-sm mr-3 mb-2" disabled={!canPageForward} onClick={() => goToPage(page + 1)}>Next</button>
    <label className="mb-2">
      Rows
      <select className="form-control form-control-sm ml-2 d-inline-block" style={{ width: 80 }} value={filters.perPage} onChange={(e) => updateFilters({ perPage: e.target.value })}>
        <option value="25">25</option>
        <option value="50">50</option>
        <option value="100">100</option>
      </select>
    </label>
  </div>;
  return <main className="container-fluid mt-3">
    <h2>Coach Match Requests</h2><p>Players who completed the Find a Coach questionnaire or tried to connect with a coach.</p>
    {error ? <div className="alert alert-danger">{error}</div> : null}
    <div className="btn-group mb-3">
      <button className={`btn ${activeTab === "queue" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => showTab("queue")}>Quiz queue</button>
      <button className={`btn ${activeTab === "activity" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => showTab("activity")}>Activity</button>
      <button className={`btn ${activeTab === "funnel" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => showTab("funnel")}>Coach funnel</button>
    </div>
    {activeTab === "funnel" ? <CoachFunnel token={token} /> : <>
    <div className="d-flex flex-wrap align-items-end mb-3">
      <input className="form-control mr-2 mb-2" style={{ maxWidth: 280 }} placeholder="Search name or email" value={filters.search} onChange={(e) => updateFilters({ search: e.target.value })} />
      {activeTab === "queue" ? <select className="form-control mr-2 mb-2" style={{ maxWidth: 160 }} value={filters.status} onChange={(e) => updateFilters({ status: e.target.value })}><option value="">All statuses</option><option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option></select> : null}
      {activeTab === "activity" ? <select className="form-control mr-2 mb-2" style={{ maxWidth: 220 }} value={filters.activityType} onChange={(e) => updateFilters({ activityType: e.target.value })}><option value="all">All activity</option><option value="quiz_completed">Quiz completions</option><option value="coach_connection_attempt">Coach connection attempts</option></select> : null}
      <label className="mr-2 mb-2" style={{ maxWidth: 190 }}>
        Submitted since
        <input className="form-control" type="date" value={filters.submittedSince} onChange={(e) => updateFilters({ submittedSince: e.target.value })} />
      </label>
      {activeTab === "queue" ? <div className="form-check mb-2">
        <input id="coach-match-unplaced-only" className="form-check-input" type="checkbox" checked={filters.hasConfirmedLesson === "false"} onChange={(e) => updateFilters({ hasConfirmedLesson: e.target.checked ? "false" : "" })} />
        <label className="form-check-label" htmlFor="coach-match-unplaced-only">Unplaced only</label>
      </div> : null}
    </div>
    {activeTab === "queue" ? <>
      <p>{data.total} request{data.total === 1 ? "" : "s"}</p>
      {pagination}
      <div className="table-responsive"><table className="table"><thead><tr><th>Submitted</th><th>Player</th><th>Contact</th><th>Player selections</th><th>Status</th><th>Outreach notes</th><th /></tr></thead><tbody>
        {data.requests.map((request) => <tr key={request.id}><td>{formatDate(request.submittedAt)}</td><td>{request.fullName}</td><td>{request.email}<br />{request.phone}</td><td>{request.selections?.length ? <ul className="mb-0 pl-3">{request.selections.map((selection) => <li key={selection.question}><strong>{selection.question}:</strong> {selection.answers.join(", ")}</li>)}</ul> : "-"}</td><td><select className="form-control" value={request.status} onChange={(e) => save(request, e.target.value)}><option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option></select></td><td><textarea className="form-control" value={notes[request.id] ?? request.outreachNotes ?? ""} onChange={(e) => setNotes({ ...notes, [request.id]: e.target.value })} /></td><td><button className="btn btn-primary" onClick={() => save(request, request.status)}>Save</button></td></tr>)}
        {!data.requests.length ? <tr><td colSpan="7">No coach match requests found.</td></tr> : null}
      </tbody></table></div>
    </> : <>
      <p>{activityData.total} activit{activityData.total === 1 ? "y" : "ies"}</p>
      {pagination}
      <div className="table-responsive"><table className="table"><thead><tr><th>When</th><th>Activity</th><th>Player</th><th>Contact</th><th>Coach</th><th>Status</th><th>Selections</th></tr></thead><tbody>
        {activityData.activities.map((activity) => <tr key={activity.id}><td>{formatDate(activity.activityAt)}</td><td>{activityLabel(activity)}</td><td>{activity.fullName}</td><td>{activity.email}<br />{activity.phone}</td><td>{activity.coachName || "-"}</td><td>{activity.status}</td><td>{activity.selections?.length ? <ul className="mb-0 pl-3">{activity.selections.map((selection) => <li key={selection.question}><strong>{selection.question}:</strong> {selection.answers.join(", ")}</li>)}</ul> : "-"}</td></tr>)}
        {!activityData.activities.length ? <tr><td colSpan="7">No coach match activity found.</td></tr> : null}
      </tbody></table></div>
    </>}
    </>}
  </main>;
}

export default CoachMatchRequests;
